import React, { useMemo } from "react";
import { useApp } from "../context/AppContext";
import SummaryCards from "./SummaryCards";
import { BalanceTrendChart, SpendingBreakdownChart, MonthlyBarChart } from "./Charts";
import { getMonthlyData, getCategorySpending, CATEGORY_COLORS } from "../data/mockData";
import { ArrowUpRight, ArrowDownRight, Clock } from "lucide-react";

export default function Dashboard() {
  const { state } = useApp();
  const monthlyData = useMemo(() => {
    const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun"];
    return months.map((month, idx) => {
      const monthNum = idx + 1;
      const txns = state.transactions.filter((t) => {
        const m = parseInt(t.date.split("-")[1]);
        return m === monthNum;
      });
      const income = txns.filter((t) => t.type === "income").reduce((s, t) => s + t.amount, 0);
      const expenses = txns.filter((t) => t.type === "expense").reduce((s, t) => s + t.amount, 0);
      return { month, income: Math.round(income), expenses: Math.round(expenses), balance: Math.round(income - expenses) };
    });
  }, [state.transactions]);

  const categoryData = useMemo(() => {
    const expense = state.transactions.filter((t) => t.type === "expense");
    const map = {};
    expense.forEach((t) => { map[t.category] = (map[t.category] || 0) + t.amount; });
    return Object.entries(map)
      .map(([name, value]) => ({ name, value: Math.round(value), color: CATEGORY_COLORS[name] || "#888" }))
      .sort((a, b) => b.value - a.value);
  }, [state.transactions]);

  const recentTxns = [...state.transactions]
    .sort((a, b) => new Date(b.date) - new Date(a.date))
    .slice(0, 5);

  return (
    <div className="space-y-6">
      <div>
        <h2 className="font-display font-bold text-2xl text-white mb-1">Overview</h2>
        <p className="text-sm font-body text-white/30">Your financial summary at a glance</p>
      </div>

      <SummaryCards transactions={state.transactions} />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2">
          <BalanceTrendChart data={monthlyData} />
        </div>
        <SpendingBreakdownChart data={categoryData} />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <MonthlyBarChart data={monthlyData} />

        <div className="bg-bg-card border border-border rounded-2xl p-5 animate-slide-up" style={{ animationDelay: "500ms" }}>
          <div className="flex items-center justify-between mb-5">
            <h3 className="font-display font-semibold text-white text-base">Recent Transactions</h3>
            <Clock size={15} className="text-white/20" />
          </div>
          <div className="space-y-3">
            {recentTxns.length === 0 ? (
              <p className="text-center text-white/20 font-body text-sm py-8">No transactions yet</p>
            ) : recentTxns.map((t, i) => (
              <div key={t.id} className="flex items-center gap-3 group">
                <div className={`w-8 h-8 rounded-xl flex items-center justify-center flex-shrink-0 ${t.type === "income" ? "bg-accent-green/10 border border-accent-green/20" : "bg-accent-red/10 border border-accent-red/20"
                  }`}>
                  {t.type === "income"
                    ? <ArrowUpRight size={14} className="text-accent-green" />
                    : <ArrowDownRight size={14} className="text-accent-red" />}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-body text-white/80 truncate">{t.description}</p>
                  <p className="text-xs font-body text-white/25">{t.category} · {t.date}</p>
                </div>
                <span className={`text-sm font-mono font-medium flex-shrink-0 ${t.type === "income" ? "text-accent-green" : "text-accent-red"
                  }`}>
                  {t.type === "income" ? "+" : "-"}${t.amount.toLocaleString()}
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
